import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users,
  Search,
  Star,
  Heart,
  ShoppingBag,
  MessageCircle,
  Send,
  Loader2,
  ChevronRight,
  Crown,
  Clock,
  TrendingUp,
  UserX,
  Bell,
  X,
  Image as ImageIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { EmptyStatePresets } from "../components/EmptyState";

export default function Customers() {
  const { getAuthHeaders, user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Detail dialog
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  
  // Message dialog
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Messages in detail view
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, getAuthHeaders());
      setCustomers(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats");
    }
  };

  const openCustomerDetail = async (customer) => {
    setSelectedCustomer(customer);
    setDetailOpen(true);
    setDetailLoading(true);
    setActiveTab("info");
    
    try {
      const response = await axios.get(`${API}/admin/customers/${customer.id}`, getAuthHeaders());
      setCustomerDetail(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des détails");
    } finally {
      setDetailLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageContent.trim() || !selectedCustomer) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/admin/customers/${selectedCustomer.id}/message`,
        { content: messageContent },
        getAuthHeaders()
      );
      
      toast.success("Message envoyé");
      setMessageContent("");
      setMessageOpen(false);
      
      // Refresh customer detail
      if (detailOpen) {
        const response = await axios.get(`${API}/admin/customers/${selectedCustomer.id}`, getAuthHeaders());
        setCustomerDetail(response.data);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Jamais";
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return (price || 0).toFixed(2) + " €";
  };

  // Filter customers by search
  const filteredCustomers = customers.filter(c => 
    c.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.contact?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gérez vos clientes et leurs préférences
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active_customers}</p>
                  <p className="text-xs text-muted-foreground">Actives (30j)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.vip_customers}</p>
                  <p className="text-xs text-muted-foreground">VIP (5+ cmd)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatPrice(stats.average_ltv)}</p>
                  <p className="text-xs text-muted-foreground">LTV moyen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher une cliente..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Customers List */}
      {filteredCustomers.length === 0 ? (
        customers.length === 0 ? (
          <EmptyStatePresets.NoCustomers 
            onAction={() => {
              // Copy vitrine link to clipboard
              const vitrineUrl = `${window.location.origin}/vitrine/${user?.id}`;
              navigator.clipboard.writeText(vitrineUrl);
              toast.success("Lien de la vitrine copié !");
            }} 
          />
        ) : (
          <EmptyStatePresets.NoSearchResults searchTerm={searchQuery} />
        )
      ) : (
        <div className="space-y-2">
          {filteredCustomers.map(customer => (
            <Card 
              key={customer.id}
              className="cursor-pointer hover:shadow-md transition-all"
              onClick={() => openCustomerDetail(customer)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-bold text-primary">
                      {customer.first_name?.charAt(0)?.toUpperCase() || "?"}
                    </span>
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium truncate">{customer.first_name}</span>
                      {customer.is_vip && (
                        <Badge className="bg-yellow-500 text-white">
                          <Crown className="w-3 h-3 mr-1" />
                          VIP
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{customer.contact}</p>
                  </div>
                  
                  {/* Stats */}
                  <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1" title="Commandes">
                      <ShoppingBag className="w-4 h-4" />
                      <span>{customer.total_orders || 0}</span>
                    </div>
                    <div className="flex items-center gap-1" title="Favoris">
                      <Heart className="w-4 h-4" />
                      <span>{customer.favorites_count || 0}</span>
                    </div>
                    <div className="flex items-center gap-1" title="Total dépensé">
                      <span className="font-medium text-foreground">
                        {formatPrice(customer.total_spent)}
                      </span>
                    </div>
                  </div>
                  
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Customer Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : customerDetail ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">
                      {customerDetail.customer.first_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {customerDetail.customer.first_name}
                      {customerDetail.customer.is_vip && (
                        <Badge className="bg-yellow-500 text-white">
                          <Crown className="w-3 h-3 mr-1" />
                          VIP
                        </Badge>
                      )}
                    </DialogTitle>
                    <DialogDescription>
                      {customerDetail.customer.contact}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Tabs */}
              <div className="flex border-b">
                {[
                  { id: "info", label: "Infos", icon: Users },
                  { id: "orders", label: "Commandes", icon: ShoppingBag },
                  { id: "favorites", label: "Favoris", icon: Heart },
                  { id: "messages", label: "Messages", icon: MessageCircle },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id 
                        ? "border-primary text-primary" 
                        : "border-transparent text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto py-4">
                {activeTab === "info" && (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <p className="text-2xl font-bold">{customerDetail.customer.total_orders || 0}</p>
                        <p className="text-xs text-muted-foreground">Commandes</p>
                      </div>
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <p className="text-2xl font-bold">{formatPrice(customerDetail.customer.total_spent)}</p>
                        <p className="text-xs text-muted-foreground">Total dépensé</p>
                      </div>
                      <div className="text-center p-4 bg-secondary rounded-lg">
                        <p className="text-2xl font-bold">{customerDetail.favorites?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Favoris</p>
                      </div>
                    </div>
                    
                    {/* Preferences */}
                    <div>
                      <h4 className="font-medium mb-3">Préférences de style</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Taille habituelle</span>
                          <span>{customerDetail.customer.preferred_size || "Non renseigné"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Style</span>
                          <span className="capitalize">{customerDetail.customer.style_preference || "Non renseigné"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Catégories préférées</span>
                          <span>{customerDetail.customer.preferred_categories?.join(", ") || "Aucune"}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Activity */}
                    <div>
                      <h4 className="font-medium mb-3">Activité</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Inscrite le</span>
                          <span>{formatDate(customerDetail.customer.created_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Dernière connexion</span>
                          <span>{formatDate(customerDetail.customer.last_login)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "orders" && (
                  <div className="space-y-2">
                    {customerDetail.orders?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Aucune commande</p>
                    ) : (
                      customerDetail.orders?.map(order => (
                        <div key={order.id} className="p-3 bg-secondary rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{order.order_number}</span>
                            <Badge variant="outline">{order.status}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {order.items?.map(item => item.product_name).join(", ")}
                          </div>
                          <div className="flex justify-between items-center mt-2 text-sm">
                            <span className="text-muted-foreground">{formatDate(order.created_at)}</span>
                            <span className="font-medium">{formatPrice(order.final_price || order.subtotal)}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === "favorites" && (
                  <div className="space-y-2">
                    {customerDetail.favorites?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Aucun favori</p>
                    ) : (
                      customerDetail.favorites?.map(fav => (
                        <div key={fav.id} className="p-3 bg-secondary rounded-lg flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{fav.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Taille souhaitée: {fav.preferred_size || "Non précisée"}
                            </p>
                          </div>
                          <span className="font-medium">{formatPrice(fav.product_price)}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === "messages" && (
                  <div className="space-y-3">
                    {customerDetail.messages?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Aucun message</p>
                    ) : (
                      customerDetail.messages?.slice().reverse().map(msg => (
                        <div 
                          key={msg.id} 
                          className={`p-3 rounded-lg max-w-[80%] ${
                            msg.sender === "customer" 
                              ? "bg-secondary ml-auto" 
                              : "bg-primary/10"
                          }`}
                        >
                          <p className="text-xs text-muted-foreground mb-1">
                            {msg.sender === "customer" ? customerDetail.customer.first_name : "Vous"}
                          </p>
                          <p className="text-sm">{msg.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(msg.created_at)}
                          </p>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Actions */}
              <DialogFooter className="border-t pt-4">
                <Button
                  onClick={() => setMessageOpen(true)}
                  className="w-full sm:w-auto"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Envoyer un message
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer un message</DialogTitle>
            <DialogDescription>
              Envoyer un message à {selectedCustomer?.first_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Votre message..."
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendMessage}
              disabled={!messageContent.trim() || sendingMessage}
            >
              {sendingMessage ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
